import { Dispatch, SetStateAction, useState } from 'react'
import * as S from './styles'
import {
  MaterialItensAction,
  TipoCriticaVolume
} from 'store/ExtraVolumeContext/Types/ExtraVolumeType'
import { Alert, Checkbox, Modal, Multiselect, Select } from 'components'
import { Controller, useForm } from 'react-hook-form'
import { useSelector } from 'store/store'
import { approveMaterials, rejectMaterials } from 'services/analysis'
import { toast } from 'react-toastify'
import { CriticismType, YesNoNotApplicable } from 'constants/enums'

interface ModalConfirmationProps {
  isOpen: boolean
  criticType: TipoCriticaVolume
  isReject: boolean
  itens: MaterialItensAction[]
  setIsOpen: Dispatch<SetStateAction<boolean>>
  handleContinue: () => void
  handleReturnAction: () => void
}

export const ModalConfirmation = ({
  isOpen = false,
  itens,
  criticType,
  setIsOpen,
  isReject,
  handleContinue,
  handleReturnAction
}: ModalConfirmationProps) => {
  const defaultValues = { cd_moti_repv_anls: '', restrito: false }
  const methods = useForm({ defaultValues })
  const { control, getValues, watch } = methods

  const [loading, setLoading] = useState(false)

  function getColors(i: MaterialItensAction[]) {
    const colorOptions = [...new Set(i.flatMap((i) => i?.cores))]
    return colorOptions as string[]
  }

  const [colors, setColors] = useState(getColors(itens))

  const { rejectionMotives } = useSelector((store) => store.extraVolume)
  const motiveList = rejectionMotives?.map((r) => ({
    value: r.cd_moti_repv_anls,
    title: r.ds_moti_repv_anls
  }))

  const handleConfirm = async () => {
    setLoading(true)

    try {
      if (isReject) {
        const { cd_moti_repv_anls, restrito } = getValues()
        const reject = await rejectMaterials({
          cd_moti_repv_anls,
          restrito: restrito
            ? YesNoNotApplicable.YES_UPPERCASE
            : YesNoNotApplicable.NO_UPPERCASE,
          itens
        })

        if (reject?.return_code === 0) {
          handleContinue()
        } else {
          toast.error(reject?.return_chav)
        }
      } else {
        const newItens = itens.map((i) => {
          if (i.cores) {
            return { ...i, cores: i?.cores?.filter((c) => colors?.includes(c)) }
          } else {
            return i
          }
        })

        const approved = await approveMaterials(newItens)

        if (approved?.return_code === 0) {
          handleContinue()
        } else {
          toast.error(approved?.return_chav)
        }
      }
    } catch {
      toast.error(
        'Algo deu errado! Tente novamente mais tarde ou entre em contato com o time de Backoffice.'
      )
    } finally {
      setLoading(false)
    }
  }

  const onChange = (e: string[]) => {
    setColors(e)
  }

  const disableButton = isReject
    ? !watch('cd_moti_repv_anls')
    : criticType !== CriticismType.QUOTA && !colors.length

  return (
    <Modal
      open={isOpen}
      setOpen={setIsOpen}
      title={isReject ? 'Reprovação' : 'Aprovação'}
      showReturn
      showClose={false}
      returnText="Voltar"
      buttonText={isReject ? 'Reprovar' : 'Aprovar'}
      buttonAction={handleConfirm}
      buttonDisabled={disableButton}
      loading={loading}
      returnAction={handleReturnAction}
    >
      <S.Wrapper>
        {isReject && (
          <>
            <S.Description>
              O material ficará em aberto no pedido, permitindo que o executivo aguarde a
              abertura de novos níveis de cota ou modifique o volume anteriormente
              solicitado.
              <br />
              <br />
              Caso marque a opção de restringir novas tentativas, o material irá para a
              aba de saldo e só poderá solicitar um volume extra para o mesmo cliente em
              um mês cota posterior.
            </S.Description>
            <S.FieldWrapper>
              <Controller
                name="cd_moti_repv_anls"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <Select
                    name={name}
                    label="Motivo ou instrução"
                    options={motiveList}
                    disabled={loading}
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            </S.FieldWrapper>
            <Controller
              name="restrito"
              control={control}
              defaultValue={false}
              render={({ field: { name, value, onChange } }) => (
                <S.WrapCheckBox>
                  <Checkbox
                    id={name}
                    checked={value}
                    disabled={loading}
                    label="Restringir tentativas deste cliente neste mês cota"
                    onChange={(e) => {
                      onChange(e.target.checked)
                    }}
                  />
                </S.WrapCheckBox>
              )}
            />
          </>
        )}
        {!isReject && (
          <>
            <S.Description>
              A liberação do volume extra será autorizada, e assim que a análise de todos
              os materiais de um pedido for concluída, o executivo será notificado e
              poderá dar continuidade à venda.
            </S.Description>
            {criticType !== CriticismType.QUOTA && (
              <>
                <Multiselect
                  width={450}
                  label="Selecione as cores que deseja aprovar"
                  optionsList={getColors(itens) as string[]}
                  disabled={loading}
                  selectedOptions={colors as string[]}
                  onChange={onChange}
                />
                <S.AlertBox>
                  <Alert
                    fullWidth
                    kind="warning"
                    title="Atenção"
                    message={
                      <S.AlertContent>
                        <li>
                          A aprovação das solicitações de volume extra gera novas demandas
                          para o plano de produção.
                        </li>
                        <li>
                          Mesmo que uma cor tenha estoque disponível, a aprovação poderá
                          gerar plano de produção para <strong>todos os materiais</strong>{' '}
                          dessa cor.
                        </li>
                      </S.AlertContent>
                    }
                  />
                </S.AlertBox>
              </>
            )}
          </>
        )}
      </S.Wrapper>
    </Modal>
  )
}
